import { Injectable, NgZone } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { take } from "rxjs/operators";

/** Shown at most once per app session for each mismatch direction. */
type MismatchKind = "epakRoute" | "paidRoute";

/**
 * Non-blocking info when route guards redirect due to platform / role mismatch.
 */
@Injectable({ providedIn: "root" })
export class PlatformMismatchNoticeService {
  private readonly snackbarDurationMs = 4000;
  private readonly shown = new Set<MismatchKind>();

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly translate: TranslateService,
    private readonly zone: NgZone,
  ) {}

  /** User has Wellvia role but tried an ePAK route — redirected to /paid. */
  notifyEpakRouteBlocked(): void {
    this.showOnce("epakRoute", "shared.platformMismatch.usePlatformTabsForEpak");
  }

  /** User has ePAK role but tried a Wellvia route — redirected to /. */
  notifyPaidRouteBlocked(): void {
    this.showOnce("paidRoute", "shared.platformMismatch.usePlatformTabsForWellvia");
  }

  /** Visible for tests. */
  resetSessionNotices(): void {
    this.shown.clear();
  }

  private showOnce(kind: MismatchKind, messageKey: string): void {
    if (this.shown.has(kind)) {
      return;
    }
    this.shown.add(kind);

    this.translate
      .get(messageKey)
      .pipe(take(1))
      .subscribe((message) => {
        this.zone.run(() => {
          this.snackBar.open(message, undefined, {
            duration: this.snackbarDurationMs,
            panelClass: ["platform-mismatch-snackbar"],
            horizontalPosition: "center",
            verticalPosition: "bottom",
          });
        });
      });
  }
}
